/**
 * Rendering primitives. Hand-rolled SVG rather than a chart library: the
 * shapes needed here are few, and a library would cost more bundle than the
 * whole app.
 */

import type { ColumnFormat, MetricColumn } from "../lib/api";
import { Fragment, type ReactNode } from "react";

export function fmt(v: unknown, f: ColumnFormat): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  switch (f) {
    case "int": return Number.isFinite(n) ? Math.round(n).toLocaleString() : String(v);
    case "float": return Number.isFinite(n) ? n.toFixed(1) : String(v);
    case "signed": return Number.isFinite(n)
      ? `${n > 0 ? "+" : ""}${Math.round(n).toLocaleString()}` : String(v);
    case "percent": return Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : String(v);
    case "position": return Number.isFinite(n) ? n.toFixed(1) : String(v);
    case "date": return String(v);
    default: return String(v);
  }
}

/** Single-series line over time. */
export function Line({ rows, xKey, yKey, format }: {
  rows: Record<string, string | null>[];
  xKey: string;
  yKey: string;
  format: ColumnFormat;
}) {
  const pts = rows
    .map((r) => ({ x: String(r[xKey]), y: Number(r[yKey]) }))
    .filter((p) => Number.isFinite(p.y));
  if (pts.length < 2) return <p className="muted">Not enough points to plot.</p>;

  const W = 720, H = 200, PAD = 34;
  const max = Math.max(...pts.map((p) => p.y));
  const min = Math.min(0, ...pts.map((p) => p.y));
  const sx = (i: number) => PAD + (i / (pts.length - 1)) * (W - PAD * 2);
  const sy = (y: number) => H - PAD - ((y - min) / (max - min || 1)) * (H - PAD * 2);

  const d = pts.map((p, i) => `${i ? "L" : "M"}${sx(i).toFixed(1)},${sy(p.y).toFixed(1)}`).join(" ");
  const area = `${d} L${sx(pts.length - 1).toFixed(1)},${H - PAD} L${sx(0).toFixed(1)},${H - PAD} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img"
         aria-label={`${yKey} over time`}>
      <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="#e6e3ee" />
      <path d={area} fill="#ede9fe" />
      <path d={d} fill="none" stroke="#5b21b6" strokeWidth="2" />
      <text x={PAD} y={16} fontSize="11" fill="#8b869e" fontFamily="JetBrains Mono, monospace">
        {fmt(max, format)}
      </text>
      <text x={PAD} y={H - 8} fontSize="11" fill="#8b869e" fontFamily="JetBrains Mono, monospace">
        {pts[0].x}
      </text>
      <text x={W - PAD} y={H - 8} fontSize="11" fill="#8b869e" textAnchor="end"
            fontFamily="JetBrains Mono, monospace">
        {pts[pts.length - 1].x}
      </text>
    </svg>
  );
}

/** Stacked bars, one bar per x value, one band per series. */
export function Stacked({ rows, xKey, seriesKey, valueKey }: {
  rows: Record<string, string | null>[];
  xKey: string;
  seriesKey: string;
  valueKey: string;
}) {
  const xs = [...new Set(rows.map((r) => String(r[xKey])))].sort();
  const series = [...new Set(rows.map((r) => String(r[seriesKey])))];
  if (!xs.length) return <p className="muted">No data in this window.</p>;

  const colors: Record<string, string> = {
    new: "#5b21b6", retained: "#c4b5fd", recovered: "#f59e0b",
  };
  const color = (s: string, i: number) =>
    colors[s] ?? ["#5b21b6", "#c4b5fd", "#f59e0b", "#8b869e"][i % 4];

  const totals = xs.map((x) =>
    rows.filter((r) => String(r[xKey]) === x)
        .reduce((a, r) => a + Number(r[valueKey] ?? 0), 0));
  const max = Math.max(...totals, 1);

  const W = 720, H = 220, PAD = 30;
  const bw = (W - PAD * 2) / xs.length;

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Cohort composition">
        {xs.map((x, xi) => {
          let acc = 0;
          return series.map((s, si) => {
            const v = Number(
              rows.find((r) => String(r[xKey]) === x && String(r[seriesKey]) === s)?.[valueKey] ?? 0);
            if (!v) return null;
            const h = (v / max) * (H - PAD * 2);
            const y = H - PAD - acc - h;
            acc += h;
            return (
              <rect key={`${x}-${s}`}
                    x={PAD + xi * bw + bw * 0.12} y={y}
                    width={bw * 0.76} height={Math.max(h, 1)}
                    fill={color(s, si)} rx="1.5">
                <title>{`${x} · ${s}: ${v.toLocaleString()}`}</title>
              </rect>
            );
          });
        })}
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="#e6e3ee" />
        <text x={PAD} y={H - 8} fontSize="11" fill="#8b869e" fontFamily="JetBrains Mono, monospace">
          {xs[0]}
        </text>
        <text x={W - PAD} y={H - 8} fontSize="11" fill="#8b869e" textAnchor="end"
              fontFamily="JetBrains Mono, monospace">
          {xs[xs.length - 1]}
        </text>
      </svg>
      <div className="legend">
        {series.map((s, i) => (
          <span key={s}>
            <span className="swatch" style={{ background: color(s, i) }} />{s}
          </span>
        ))}
      </div>
    </>
  );
}

/**
 * Sortable table. Headers carry their own definition, because a column named
 * "ceiling gap" means nothing without one. Rows expand in place when the
 * metric identifies an entity with a history worth seeing.
 */
export function Table({
  rows, columns, sortKey, sortDesc = true, onSort, entityKey, expandedRow,
  onToggleRow, renderDetail,
}: {
  rows: Record<string, string | null>[];
  columns: MetricColumn[];
  sortKey: string | null;
  sortDesc?: boolean;
  onSort: (k: string) => void;
  /** Column holding the entity value, when rows are expandable. */
  entityKey?: string;
  expandedRow?: string | null;
  onToggleRow?: (value: string) => void;
  renderDetail?: (value: string) => ReactNode;
}) {
  if (!rows.length) return <p className="muted">No rows matched in this window.</p>;
  const expandable = Boolean(entityKey && onToggleRow);

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}
                  className={`${c.format === "text" ? "" : "r"} ${sortKey === c.key ? "sorted" : ""}`}
                  aria-sort={sortKey === c.key ? (sortDesc ? "descending" : "ascending") : "none"}
                  onClick={() => onSort(c.key)}>
                <span className="hdr-label">
                  {c.label}
                  <span className="hdr-arrow">
                    {sortKey === c.key ? (sortDesc ? "↓" : "↑") : "↕"}
                  </span>
                </span>
                <span className="hdr-desc">{c.help}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 100).map((r, i) => {
            const value = entityKey ? String(r[entityKey] ?? "") : "";
            const open = expandable && expandedRow === value;
            return (
              <Fragment key={`${value}-${i}`}>
                <tr className={`${expandable ? "expandable" : ""} ${open ? "open" : ""}`}
                    tabIndex={expandable ? 0 : undefined}
                    role={expandable ? "button" : undefined}
                    aria-expanded={expandable ? open : undefined}
                    onClick={expandable ? () => onToggleRow!(value) : undefined}
                    onKeyDown={expandable ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onToggleRow!(value);
                      }
                    } : undefined}>
                  {columns.map((c) => (
                    <td key={c.key}
                        className={c.format === "text" ? "cell-text" : "r num"}
                        title={c.format === "text" ? String(r[c.key] ?? "") : undefined}>
                      {fmt(r[c.key], c.format)}
                    </td>
                  ))}
                </tr>
                {open && (
                  <tr>
                    <td className="det-cell" colSpan={columns.length}>
                      {renderDetail?.(value)}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {rows.length > 100 && (
        <p className="caveat">Showing 100 of {rows.length.toLocaleString()} rows.</p>
      )}

    </div>
  );
}
